import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';

// Admin → Contacts: the branches a contact can be placed in (Calem, report 7be30766). Each member of staff and each
// client can carry one, picked on the contact form's Branch location; the Contacts list shows it in brackets after the
// name. The list is a setting (app_settings 'contact_branches', services/contactBranch.ts), not names in the code, and
// is saved whole with Save, which puts it in alphabetical order — the order the form lists it in. Taking a branch off
// the list only takes it off the form's choices: a contact already placed in it keeps it until it is changed.

const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '6px 12px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color,
  fontSize: 12.5, fontWeight: 600, cursor: 'pointer',
});
const field: React.CSSProperties = {
  padding: '6px 8px', fontSize: 12.5, borderRadius: 8, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none',
};
const iconBtn: React.CSSProperties = { border: 'none', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', padding: '2px 4px' };
// The server takes at most this many (saveContactBranches) — a refused Save would page the admins.
const MAX_BRANCHES = 50;

export default function ContactBranches() {
  const { t } = useTranslation();
  const [rows, setRows] = useState<string[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.contactBranches().then(d => setRows(d?.branches || [])).catch((e: any) => setErr(e.message));
  }, []);

  const edit = (next: (rs: string[]) => string[]) => { setDone(false); setErr(''); setRows(rs => next(rs || [])); };

  // The server would merge the same name typed twice; it is asked about here instead, so nobody's spelling is dropped
  // unseen. Blank rows are simply left out.
  const problem = (): string => {
    const seen = new Set<string>();
    for (const r of rows || []) {
      const k = r.replace(/\s+/g, ' ').trim().toLowerCase();
      if (!k) continue;
      if (seen.has(k)) return t('cbr_twice', { name: r.trim() });
      seen.add(k);
    }
    return '';
  };
  const save = async () => {
    const why = problem();
    if (why) { setErr(why); return; }
    setBusy(true); setErr('');
    try {
      const d = await api.adminSetContactBranches((rows || []).map(r => r.trim()).filter(Boolean));
      setRows(d.branches || []); setDone(true);
    } catch (e: any) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ maxWidth: 820, margin: '0 auto' }}>
      <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 16, background: 'var(--bg-surface)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <i className="ti ti-map-pin" style={{ fontSize: 20, color: 'var(--text-secondary)' }} aria-hidden />
          <div style={{ flex: 1, fontSize: 14, fontWeight: 800, color: 'var(--text-primary)' }}>{t('cbr_title')}</div>
          <button onClick={() => edit(rs => [...rs, ''])} disabled={rows === null || rows.length >= MAX_BRANCHES}
            style={{ ...btn('var(--c-gray-100)', 'var(--text-secondary)'), whiteSpace: 'nowrap' }}>
            <i className="ti ti-plus" aria-hidden /> {t('cbr_add')}
          </button>
        </div>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>{t('cbr_desc')}</div>

        {rows === null ? (
          !err && <div style={{ padding: 12, textAlign: 'center', color: 'var(--text-tertiary)' }}><i className="ti ti-loader" aria-hidden /></div>
        ) : rows.length === 0 ? (
          <div style={{ fontSize: 12.5, color: 'var(--text-tertiary)', padding: '10px 0', borderTop: '1px solid var(--border)' }}>{t('cbr_none')}</div>
        ) : rows.map((r, i) => (
          <div key={i} style={{ borderTop: '1px solid var(--border)', padding: '8px 0', display: 'flex', alignItems: 'center', gap: 8 }}>
            <input value={r} onChange={e => { const v = e.target.value; edit(rs => rs.map((x, j) => (j === i ? v : x))); }} maxLength={40}
              placeholder={t('cbr_name')} style={{ ...field, flex: 1, maxWidth: 320 }} />
            <div style={{ flex: 1 }} />
            <button onClick={() => edit(rs => rs.filter((_, j) => j !== i))} title={t('delete')} style={{ ...iconBtn, color: '#E24B4A' }}><i className="ti ti-trash" aria-hidden /></button>
          </div>
        ))}

        {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginTop: 10 }}>{err}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          {done && <span style={{ fontSize: 12, color: '#15803D' }}>{t('cbr_saved')}</span>}
          <button onClick={save} disabled={busy || rows === null} style={{ ...btn('var(--accent)'), opacity: busy || rows === null ? 0.6 : 1 }}>
            {busy ? t('login_wait') : t('save')}
          </button>
        </div>
      </div>
    </div>
  );
}
